import {
  AddonCadence,
  AddonCode,
  Business,
  PlanCode,
  SubscriptionInterval,
} from '@hero/core';
import {
  Billing,
  businessSchema,
  currencySchema,
  objectIdSchema,
  paginationWithSearchSchema,
  subscriptionGrantModeSchema,
  subscriptionModelSchema,
  subscriptionStatusSchema,
  SubscriptionBalance,
  SubscriptionModel,
  SubscriptionPublic,
  type BillingUnit,
} from '@hero/schema';
import { z } from 'zod';
import { API } from '../..';

/**
 * Which collection holds a subscription's owner — an `org`, or the
 * `user` login a freelancer owner-operator bills as. Threaded through
 * every admin credit action so a grant lands on the right party's
 * bucket; the resolver (`SubscriptionOwnerService.resolve`) branches on
 * it, and creating a fresh bucket for a `user` id as an `org` would
 * throw. Defaults to `org` for back-compat with existing call sites.
 */
export const subscriptionOwnerTypeSchema = z.enum(['org', 'user']);
export type SubscriptionOwnerType = z.infer<typeof subscriptionOwnerTypeSchema>;

/**
 * Grant filter for the admin Subscriptions directory — narrows the list
 * to owners holding a still-live grant (not revoked, not expired):
 *
 * - `free` — has an active `free` gift (unspent goodwill / promo credits).
 * - `repayable` — has an active `repayable` overdraft awaiting settlement.
 * - `any` — has an active grant of either kind.
 *
 * Omit it to list every subscription regardless of grants.
 */
export const subscriptionGrantFilterSchema = z.enum(['free', 'repayable', 'any']);
export type SubscriptionGrantFilter = z.infer<typeof subscriptionGrantFilterSchema>;

/**
 * GET /subscription/me — return the calling org's subscription, or null if
 * the org is on the implicit free tier.
 */
/** `business` — which brand's subscription; an owner may hold one per business. Default `hero`. */
export const subscriptionGetMineAPISchema = z.object({ business: businessSchema }).partial();
export type SubscriptionGetMineAPI = API<
  z.infer<typeof subscriptionGetMineAPISchema>,
  SubscriptionPublic | null
>;

/**
 * GET /subscription/usage — what the calling org has consumed this
 * period, live.
 *
 * One read serving three callers that must not disagree: the
 * entitlement gate deciding whether to block, the dashboard showing
 * "38 of 50 orders", and the checkout picking which basis tab to open
 * on. All three come off the same aggregation the renewal invoice
 * prices from, so a customer can never be told one number and charged
 * against another.
 *
 * Both meters come back regardless of the org's basis (they are two
 * cheap queries) — a checkout choosing which way of paying to lead with
 * needs the one the org is NOT currently on.
 *
 * Answers for an org with no subscription too: window falls back to the
 * calendar month, `committedQty` is 0. The free tier still needs a
 * usage number for the paywall to count an allowance against.
 */
export const subscriptionGetUsageAPISchema = z.object({ business: businessSchema }).partial();
export type SubscriptionGetUsageAPI = API<
  z.infer<typeof subscriptionGetUsageAPISchema>,
  {
    /** What the org's current plan meters. `vehicle` with no subscription. */
    unit: BillingUnit;
    /**
     * Which of the two answers below is the real one — see
     * `SubscriptionModel`. On `usage`, read `balance`; the period
     * fields describe a calendar month that nothing is billed against.
     */
    model: SubscriptionModel;
    /** The window `orderCount` was measured over. */
    periodStart: Date;
    periodEnd: Date;
    /** Committed quantity for the metered basis. 0 when uncommitted. */
    committedQty: number;
    /**
     * Billable orders created in the window — excludes the
     * cancelled-within-grace-and-never-filed ones. See
     * `SubscriptionService.countOrdersInPeriod`.
     *
     * Informational on a `usage` subscription: what governs there is
     * `balance.remaining`, not a count over a window. Kept because
     * "how many did we ship this month" is a question customers ask
     * whichever way they pay.
     */
    orderCount: number;
    /** Live vehicle headcount, no window. */
    vehicleCount: number;
    /**
     * The credit bucket, on a `usage` subscription. Absent on
     * `period`, which has no credits to report.
     */
    balance?: SubscriptionBalance;
  }
>;

/**
 * GET /subscription/admin — admin lookup of an owner's subscription.
 *
 * `ownerType` selects the collection (`org` default, or the `user`
 * login a freelancer bills as). The lookup is by owner `_id`, so this
 * returns the owner's most-recent grant; use {@link SubscriptionAdminGetAllAPI}
 * to page every subscription across the platform.
 */
export const subscriptionGetAdminAPISchema = z.object({
  ownerId: objectIdSchema,
  ownerType: subscriptionOwnerTypeSchema.default('org'),
  business: businessSchema.optional(),
});
export type SubscriptionGetAdminAPI = API<
  z.infer<typeof subscriptionGetAdminAPISchema>,
  SubscriptionPublic | null
>;

/**
 * GET /subscription/admin/all — admin paginated list of every
 * subscription across the platform (both `usage` credit buckets and
 * `period` seat plans), newest first. `search` matches the owner name;
 * `business` / `model` / `status` / `grants` narrow the list for the
 * admin Subscriptions directory.
 *
 * `ownerId` narrows to one party. An owner holds one bucket per
 * `(business, unit)` — a Hero seat plan, Hero order credits and a
 * Wathiqa Plus grant are three separate documents — so the drawer that
 * shows "this org's subscriptions" must list them all. Use this rather
 * than {@link SubscriptionGetAdminAPI}, which returns whichever one the
 * database hands back first.
 */
export const subscriptionAdminGetAllAPISchema = z
  .object({
    /** One party's buckets only — an Org `_id` or a freelancer's User `_id`. */
    ownerId: objectIdSchema.optional(),
    /** Brand that sold the subscription — filters on `purchase.business`. */
    business: businessSchema.optional(),
    /** `usage` (prepaid credits) vs `period` (recurring seats). */
    model: subscriptionModelSchema.optional(),
    status: subscriptionStatusSchema.optional(),
    /**
     * Only owners holding a still-live grant of this kind — see
     * {@link subscriptionGrantFilterSchema}. Use it to surface the
     * subscriptions that were handed free credits or a repayable
     * overdraft.
     */
    grants: subscriptionGrantFilterSchema.optional(),
  })
  .extend(paginationWithSearchSchema.shape);
export type SubscriptionAdminGetAllAPI = API<
  z.infer<typeof subscriptionAdminGetAllAPISchema>,
  SubscriptionPublic[]
>;

/**
 * POST /subscription — start a new subscription (creates first Billing + checkout
 * session in one shot).
 */
export const subscriptionSubscribeAPISchema = z.object({
  orgId: objectIdSchema,
  planCode: z.string() as z.ZodType<PlanCode>,
  interval: z.enum(['month', 'semester', 'year']) as z.ZodType<SubscriptionInterval>,
  committedVehicleQty: z.coerce.number().int().min(0),
  /**
   * Committed orders per month. Required when the plan's `unit` is
   * `order` — the backend rejects a paid selection whose unit has
   * no quantity (`subscription_missing_committed_qty`) rather than
   * silently creating a subscription that prices to zero.
   */
  committedOrderQty: z.coerce.number().int().min(0).optional(),
  currency: currencySchema.optional(),
  addons: z
    .array(
      z.object({
        code: z.string() as z.ZodType<AddonCode>,
        billing: z.enum(['recurring', 'one_time']),
        quantity: z.coerce.number().int().min(1).default(1),
      }),
    )
    .default([]),
  callbackUrl: z.string().url(),
  successUrl: z.string().url().optional(),
});
export type SubscriptionSubscribeAPI = API<
  z.infer<typeof subscriptionSubscribeAPISchema>,
  {
    subscription: SubscriptionPublic;
    checkoutUrl: string | undefined;
  }
>;

/**
 * POST /subscription/pay — single user-initiated payment entry point
 * for an org that **already has a Subscription** (trialing / active /
 * past_due). Mirrors the `subscribe` payload shape minus `orgId`
 * (which the backend resolves from auth).
 *
 * - `trialing` → backend returns a `checkoutUrl` for Moyasar handoff.
 * - `active` / `past_due` with saved token → silent charge, no
 *   `checkoutUrl` returned; `charged` is `true`.
 * - downgrade / no-money-owed → field update only; `billing` is `null`,
 *   `charged` is `false`, no `checkoutUrl`.
 */
export const subscriptionPayAPISchema = z.object({
  planCode: z.string() as z.ZodType<PlanCode>,
  interval: z.enum(['month', 'semester', 'year']) as z.ZodType<SubscriptionInterval>,
  committedVehicleQty: z.coerce.number().int().min(0),
  /** Committed orders per month. Required when the plan unit is `order`. */
  committedOrderQty: z.coerce.number().int().min(0).optional(),
  callbackUrl: z.string().url(),
  successUrl: z.string().url().optional(),
});
export type SubscriptionPayAPI = API<
  z.infer<typeof subscriptionPayAPISchema>,
  {
    subscription: SubscriptionPublic;
    billing: Billing | null;
    charged: boolean;
    checkoutUrl: string | undefined;
  }
>;

/**
 * PATCH /subscription/plan — change plan (effective next period in v1).
 */
export const subscriptionChangePlanAPISchema = z.object({
  _id: objectIdSchema,
  planCode: z.string() as z.ZodType<PlanCode>,
});
export type SubscriptionChangePlanAPI = API<
  z.infer<typeof subscriptionChangePlanAPISchema>,
  SubscriptionPublic
>;

/**
 * PATCH /subscription/interval — change interval (effective next period).
 */
export const subscriptionChangeIntervalAPISchema = z.object({
  _id: objectIdSchema,
  interval: z.enum(['month', 'semester', 'year']) as z.ZodType<SubscriptionInterval>,
});
export type SubscriptionChangeIntervalAPI = API<
  z.infer<typeof subscriptionChangeIntervalAPISchema>,
  SubscriptionPublic
>;

/**
 * PATCH /subscription/seats — resize the commitment.
 *
 * Send whichever quantity the subscription's basis meters; the backend
 * writes only the field(s) present, so resizing volume can't zero out a
 * seat count. Sending neither is rejected
 * (`subscription_invalid_seat_change`).
 */
export const subscriptionChangeSeatsAPISchema = z.object({
  _id: objectIdSchema,
  committedVehicleQty: z.coerce.number().int().min(0).optional(),
  committedOrderQty: z.coerce.number().int().min(0).optional(),
});
export type SubscriptionChangeSeatsAPI = API<
  z.infer<typeof subscriptionChangeSeatsAPISchema>,
  SubscriptionPublic
>;

/**
 * POST /subscription/addon — add or update an addon.
 */
export const subscriptionAddAddonAPISchema = z.object({
  _id: objectIdSchema,
  code: z.string() as z.ZodType<AddonCode>,
  billing: z.enum(['recurring', 'one_time']),
  quantity: z.coerce.number().int().min(1).default(1),
  callbackUrl: z.string().url().optional(),
});
export type SubscriptionAddAddonAPI = API<
  z.infer<typeof subscriptionAddAddonAPISchema>,
  SubscriptionPublic
>;

/**
 * DELETE /subscription/addon — remove an addon (stops at period end for recurring).
 */
export const subscriptionRemoveAddonAPISchema = z.object({
  _id: objectIdSchema,
  code: z.string() as z.ZodType<AddonCode>,
});
export type SubscriptionRemoveAddonAPI = API<
  z.infer<typeof subscriptionRemoveAddonAPISchema>,
  SubscriptionPublic
>;

/**
 * POST /subscription/cancel — cancel at end of current period (default) or immediately.
 */
export const subscriptionCancelAPISchema = z.object({
  _id: objectIdSchema,
  atPeriodEnd: z.boolean().default(true),
  reason: z.string().max(500).optional(),
});
export type SubscriptionCancelAPI = API<
  z.infer<typeof subscriptionCancelAPISchema>,
  SubscriptionPublic
>;

/**
 * PATCH /subscription/admin/override — set pricing override on a sub.
 */
export const subscriptionAdminOverrideAPISchema = z.object({
  _id: objectIdSchema,
  pricingOverride: z
    .object({
      basePricePerMonth: z.coerce.number().min(0).optional(),
      perVehiclePricePerMonth: z.coerce.number().min(0).optional(),
      overagePerVehiclePerMonth: z.coerce.number().min(0).optional(),
      /** Price of one order — for `order`-unit subscriptions. */
      perOrderPrice: z.coerce.number().min(0).optional(),
      overagePerOrderPrice: z.coerce.number().min(0).optional(),
      note: z.string().max(500).optional(),
    })
    .optional(),
  addonOverrides: z
    .record(
      z.string(),
      z.object({
        pricing: z
          .object({
            oneTime: z.coerce.number().min(0).optional(),
            week: z.coerce.number().min(0).optional(),
            month: z.coerce.number().min(0).optional(),
            semester: z.coerce.number().min(0).optional(),
            year: z.coerce.number().min(0).optional(),
          })
          .optional(),
      }),
    )
    .optional(),
});
export type SubscriptionAdminOverrideAPI = API<
  z.infer<typeof subscriptionAdminOverrideAPISchema>,
  SubscriptionPublic
>;

/**
 * POST /subscription/admin/renew — force a renewal cycle (for testing / admin recovery).
 */
export const subscriptionAdminForceRenewAPISchema = z.object({
  _id: objectIdSchema,
});
export type SubscriptionAdminForceRenewAPI = API<
  z.infer<typeof subscriptionAdminForceRenewAPISchema>,
  SubscriptionPublic
>;

/**
 * POST /subscription/admin/grant-credits — hand an org a bucket of
 * credits outside a purchase (a promo or a goodwill gift).
 *
 * `mode: 'free'` is a true gift (never charged); `mode: 'repayable'` is
 * the try-it-then-pay overdraft that the next purchase settles.
 * `expiresAt` is optional — omit it for a grant that lasts until spent.
 */
export const subscriptionAdminGrantCreditsAPISchema = z.object({
  /** The owner receiving the credits — an Org or a freelancer's User. */
  ownerId: objectIdSchema,
  ownerType: subscriptionOwnerTypeSchema.default('org'),
  /** Which business's credit bucket to grant into (e.g. `wathiqa_plus`, `hero`). */
  business: z.string() as z.ZodType<Business>,
  qty: z.coerce.number().int().min(1),
  mode: subscriptionGrantModeSchema,
  /** When the grant lapses. Absent = open-ended, lives until spent. */
  expiresAt: z.coerce.date().optional(),
  reason: z.string().max(500).optional(),
  /**
   * Send the `wathiqa_free_orders` WhatsApp template after a free
   * Wathiqa Plus grant. Defaults on — staff can uncheck in the drawer.
   */
  notify: z.boolean().default(true),
});
export type SubscriptionAdminGrantCreditsAPI = API<
  z.infer<typeof subscriptionAdminGrantCreditsAPISchema>,
  SubscriptionPublic | null
>;

/**
 * POST /subscription/admin/revoke-credits — revoke an org's outstanding
 * grants, reclaiming only the unspent free credits and dropping any
 * repayable overdraft. `mode` narrows it; `business` scopes it.
 */
export const subscriptionAdminRevokeCreditsAPISchema = z.object({
  /** The owner whose grants to revoke — an Org or a freelancer's User. */
  ownerId: objectIdSchema,
  ownerType: subscriptionOwnerTypeSchema.default('org'),
  business: (z.string() as z.ZodType<Business>).optional(),
  mode: subscriptionGrantModeSchema.optional(),
});
export type SubscriptionAdminRevokeCreditsAPI = API<
  z.infer<typeof subscriptionAdminRevokeCreditsAPISchema>,
  { revoked: number }
>;

/**
 * POST /subscription/admin/reset-free-credits — the global "make
 * everyone zero". Revokes every outstanding grant across all owners,
 * reclaiming unspent free credits and dropping repayable overdrafts;
 * purchased (paid) credits are untouched. Destructive and super-admin
 * only, so it requires an explicit `confirm: true`.
 */
export const subscriptionAdminResetFreeCreditsAPISchema = z.object({
  confirm: z.literal(true),
});
export type SubscriptionAdminResetFreeCreditsAPI = API<
  z.infer<typeof subscriptionAdminResetFreeCreditsAPISchema>,
  { subscriptions: number; revoked: number }
>;

/**
 * POST /subscription/payment-method/checkout — hosted checkout to save/replace card.
 */
export const subscriptionPaymentMethodCheckoutAPISchema = z.object({
  callbackUrl: z.string().url(),
  successUrl: z.string().url().optional(),
});
export type SubscriptionPaymentMethodCheckoutAPI = API<
  z.infer<typeof subscriptionPaymentMethodCheckoutAPISchema>,
  { checkoutUrl: string }
>;

/**
 * DELETE /subscription/payment-method — clear saved card on caller's subscription.
 */
export const subscriptionPaymentMethodDeleteAPISchema = z.object({}).partial();
export type SubscriptionPaymentMethodDeleteAPI = API<
  z.infer<typeof subscriptionPaymentMethodDeleteAPISchema>,
  SubscriptionPublic
>;

export type SubscriptionAddonCadence = AddonCadence;
